import React, { useMemo, useState } from 'react';
import {
 Elements, PaymentElement, useElements, useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { StripePaymentIntent } from '@/features/rapidstor/services/R6PayService';

import Button from '@/core/components/elements/button';
import Spinner from '@/core/components/elements/spinner';

import classes from './styles.module.scss';

interface StripeFormInnerProps {
    returnUrl: string;
    onError: (message: string) => void;
    buttonText?: string;
}

function StripeFormInner({ returnUrl, onError, buttonText = 'Confirm Payment' }: StripeFormInnerProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setSubmitting(true);

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: returnUrl },
            // Stay on page if payment completes without a redirect (e.g. no 3DS)
            redirect: 'if_required',
        });

        if (error) {
            onError(error.message ?? 'Payment failed. Please try again.');
            setSubmitting(false);
            return;
        }

        // No redirect means payment succeeded in-page — navigate to return_url manually
        window.location.href = returnUrl;
    };

    return (
        <form onSubmit={handleSubmit} className={classes.form}>
            <PaymentElement />
            <Button
                type="primary"
                size="medium"
                disableScroll
                disabled={submitting || !stripe}
            >
                {submitting ? <Spinner size="25" inverted /> : buttonText}
            </Button>
        </form>
    );
}

interface StripePaymentFormProps {
    intent: StripePaymentIntent;
    onError: (message: string) => void;
    buttonText?: string;
}

export default function StripePaymentForm({ intent, onError, buttonText }: StripePaymentFormProps) {
    // Elements tears down and remounts (including the wallet/Apple Pay button) whenever it
    // receives a new stripe promise, so this must stay stable across re-renders of the parent form.
    const stripePromise = useMemo(() => loadStripe(intent.publishableKey), [intent.publishableKey]);

    return (
        <Elements
            stripe={stripePromise}
            options={{ clientSecret: intent.clientSecret }}
        >
            <StripeFormInner returnUrl={intent.returnUrl} onError={onError} buttonText={buttonText} />
        </Elements>
    );
}
